import { AbsoluteModelPath, createFromMask, ModelPath, PathTokens } from "../../../../../../Common/ModelPath/ModelPath";
import { ICustomValidationFunctionCallContextProvider } from "../../../Form/CustomizationContexts/ValidatorContext/ICustomValidationFunctionCallContextProvider";
import { IErrorManager } from "../../ErrorManager/ErrorManager";
import { Localization } from "../../Localization/Localization";
import { IModel } from "../../ModelStoring/Model";
import { IPicklistsProvider } from "../../Picklists/IPicklistsProvider";
import { FormStore } from "../../Store/CandyStore";
import { LegacyValidationFunction } from "../../Validator/ValidatorResources";
import { IValueValidator } from "../../Validator/ValueValidators/Bases/IValueValidator";
import { CalculationFunctionBase } from "../AutoCalculations/CalculationFunctionBase";
import { CalculationFunctionDependency } from "../CalculationFunctionDependency";
import { CalculationTarget, CalculationTargetAttribute } from "../CalculationTarget";
import { IEvaluationContext } from "../IEvaluationContext";

export class LegacyValidationFunctionAdapter extends CalculationFunctionBase {
    private readonly calculationTarget: CalculationTarget;
    private readonly dependencies: readonly CalculationFunctionDependency[];
    private readonly validatorPathMask: string;
    private readonly enableLegacyValidators: boolean;
    private readonly customValidationFunctionCallContextProvider: ICustomValidationFunctionCallContextProvider;
    private readonly getIsValidationsDisabled: () => boolean;
    private readonly localization: Localization;
    private readonly store: FormStore;
    private readonly errorManager: IErrorManager;
    private readonly picklistsProvider: IPicklistsProvider;
    private readonly validator: IValueValidator | LegacyValidationFunction;
    private readonly condition: undefined | ((evaluationContext: IEvaluationContext) => boolean);

    public constructor(
        validatorPathMask: string,
        validator: IValueValidator | LegacyValidationFunction,
        localization: Localization,
        store: FormStore,
        errorManager: IErrorManager,
        picklistsProvider: IPicklistsProvider,
        customValidationFunctionCallContextProvider: ICustomValidationFunctionCallContextProvider,
        enableLegacyValidators: boolean,
        getIsValidationsDisabled: () => boolean,
        deps: AbsoluteModelPath[] | undefined,
        condition: undefined | ((evaluationContext: IEvaluationContext) => boolean)
    ) {
        const targetPath = createFromMask(validatorPathMask, true, PathTokens.each);
        super(targetPath);
        this.condition = condition;
        this.validatorPathMask = validatorPathMask;
        this.enableLegacyValidators = enableLegacyValidators;
        this.customValidationFunctionCallContextProvider = customValidationFunctionCallContextProvider;
        this.getIsValidationsDisabled = getIsValidationsDisabled;
        this.localization = localization;
        this.store = store;
        this.errorManager = errorManager;
        this.picklistsProvider = picklistsProvider;
        this.validator = validator;
        this.calculationTarget = new CalculationTarget(targetPath, CalculationTargetAttribute.Validation);

        const dependenciesList = [createFromMask(validatorPathMask, true, PathTokens.current)];
        if (deps != undefined) {
            dependenciesList.push(...deps);
        }
        this.dependencies = dependenciesList.map(x => new CalculationFunctionDependency(targetPath, x));
    }

    public asDebugString(): string {
        if ("validate" in this.validator) {
            return this.validator.asDebugString();
        } else {
            return "[ValidatorOnlyForEngineApprovalTests]";
        }
    }

    public getDependencies(): readonly CalculationFunctionDependency[] {
        return this.dependencies;
    }

    public isDependsOn(calculationTarget: CalculationTarget): boolean {
        return (
            calculationTarget.targetAttribute === CalculationTargetAttribute.Value &&
            this.dependencies.some(x => x.isMatchToPath(calculationTarget.modelPath))
        );
    }

    public getTarget(): CalculationTarget {
        return this.calculationTarget;
    }
    public async execute(context: IEvaluationContext): Promise<void> {
        if (this.getIsValidationsDisabled()) {
            context.setError(this.targetPath, undefined);
            return;
        }

        if (this.condition != undefined) {
            const condResult = this.condition(context);
            if (!condResult) {
                context.setError(this.targetPath, undefined);
                return;
            }
        }
        const resolvedTargetPath = context.resolvePath(this.targetPath);
        if (context.isIgnoredPath(resolvedTargetPath)) {
            return;
        }
        if ("validate" in this.validator) {
            const result = await this.validator.validate(
                {
                    localizer: this.localization,
                    store: this.store,
                    path: resolvedTargetPath.toLegacyPath(),
                    customValidationFunctionCallContextProvider: this.customValidationFunctionCallContextProvider,
                    errorManager: this.errorManager,
                    model: context.model,
                    picklistsProvider: this.picklistsProvider,
                    evaluationContext: context,
                },
                {
                    value: context.get(createFromMask(this.validatorPathMask, true, PathTokens.current)),
                    autoValue: context.getAutoValue(createFromMask(this.validatorPathMask, true, PathTokens.current)),
                }
            );
            if (result.valid) {
                context.setError(this.targetPath, undefined);
            } else {
                if (result.type === "error") {
                    context.setError(this.targetPath, result.message ?? "");
                } else {
                    context.setWarning(this.targetPath, result.message ?? "");
                }
            }
        } else {
            const evaluationResult = this.executeValidatorOnlyForEngineApprovalTests(
                context.model,
                this.validator,
                resolvedTargetPath
            );
            context.setError(this.targetPath, evaluationResult);
        }
    }

    private executeValidatorOnlyForEngineApprovalTests(
        model: IModel,
        validator: LegacyValidationFunction,
        targetPath: ModelPath
    ): string | undefined {
        if (!this.enableLegacyValidators) {
            throw new Error("Legacy validation functions is disabled");
        }
        let errorMessage: string | undefined;
        validator.call(
            {
                ...this.customValidationFunctionCallContextProvider.getCustomValidationFunctionCallContext(model),
                errorManager: {
                    addError: (_: string, message: string) => {
                        errorMessage = message;
                    },
                    addErrorCached: (_: string, message: string) => {
                        errorMessage = message;
                    },
                    addWarning: (): void => {
                        // none
                    },
                    clearErrors: (): void => {
                        // none
                    },
                    clearWarnings: (): void => {
                        // none
                    },
                    removeWarning: (): void => {
                        // none
                    },
                },
            },
            targetPath.toLegacyPath()
        );
        return errorMessage;
    }
}
